/**
 * Content Block Fragments
 *
 * This file exports all block fragments and composes them into
 * a reusable contentBlockFragment for use in page/location queries.
 *
 * Each block has:
 * - A field projection (for composition)
 * - A complete query (for type generation)
 */

import { defineQuery } from 'next-sanity'

// Import all block fragments (for exports)
export {
  contactFormBlockFields,
  CONTACT_FORM_BLOCK_QUERY,
} from './contactFormBlock'
export { expertCarouselFields, EXPERT_CAROUSEL_QUERY } from './expertCarousel'
export {
  heroSliderBannerFields,
  HERO_SLIDER_BANNER_QUERY,
} from './heroSliderBanner'
export { copyBlockFields, COPY_BLOCK_QUERY } from './copyBlock'
export { imageStackFields, IMAGE_STACK_QUERY } from './imageStack'
export {
  tabbedAccordionFields,
  TABBED_ACCORDION_QUERY,
} from './tabbedAccordion'
export { pricePlansFields, PRICE_PLANS_QUERY } from './pricePlans'
export {
  tabbedMediaCardBlockFields,
  TABBED_MEDIA_CARD_BLOCK_QUERY,
} from './tabbedMediaCardBlock'
export { featureBannerFields, FEATURE_BANNER_QUERY } from './featureBanner'
export {
  splitCopyImageBlockFields,
  SPLIT_COPY_IMAGE_BLOCK_QUERY,
} from './splitCopyImageBlock'
export { fileContentFields, FILE_CONTENT_QUERY } from './fileContent'
export { twoCardsBlockFields, TWO_CARDS_BLOCK_QUERY } from './twoCardsBlock'
export {
  puzzlePiecesBlockFields,
  PUZZLE_PIECES_BLOCK_QUERY,
} from './puzzlePiecesBlock'
export { skillsBlockFields, SKILLS_BLOCK_QUERY } from './skillsBlock'
export { quoteBlockFields, QUOTE_BLOCK_QUERY } from './quoteBlock'
export { subjectSliderFields, SUBJECT_SLIDER_QUERY } from './subjectSlider'
export { videoHeroFields, VIDEO_HERO_QUERY } from './videoHero'
export { postCardBlockFields, POST_CARD_BLOCK_QUERY } from './postCardBlock'
export { dayNurseriesFields, DAY_NURSERIES_QUERY } from './dayNurseries'
export { newsBlockFields, NEWS_BLOCK_QUERY } from './newsBlock'
export { igPostsFields, IG_POSTS_QUERY } from './igPosts'
export { gridBlockFields, GRID_BLOCK_QUERY } from './gridBlock'
export {
  nurserySearchBlockFields,
  NURSERY_SEARCH_BLOCK_QUERY,
} from './nurserySearchBlock'
export {
  regionCardsBlockFields,
  REGION_CARDS_BLOCK_QUERY,
} from './regionCardsBlock'
export {
  careersJobListingsBlockFields,
  CAREERS_JOB_LISTINGS_BLOCK_QUERY,
} from './careersJobListingsBlock'
export {
  careersHeroBlockFields,
  CAREERS_HERO_BLOCK_QUERY,
} from './careersHeroBlock'
export {
  careersRoleLinksBlockFields,
  CAREERS_ROLE_LINKS_BLOCK_QUERY,
} from './careersRoleLinksBlock'
export {
  careersAfterHeroBlockFields,
  CAREERS_AFTER_HERO_BLOCK_QUERY,
} from './careersAfterHeroBlock'
export {
  careersTextBlockFields,
  CAREERS_TEXT_BLOCK_QUERY,
} from './careersTextBlock'
export {
  careersTextImageBlockFields,
  CAREERS_TEXT_IMAGE_BLOCK_QUERY,
} from './careersTextImageBlock'
export {
  careersTextCollageBlockFields,
  CAREERS_TEXT_COLLAGE_BLOCK_QUERY,
} from './careersTextCollageBlock'
export {
  careersTextColumnsBlockFields,
  CAREERS_TEXT_COLUMNS_BLOCK_QUERY,
} from './careersTextColumnsBlock'
export {
  careersTextAccordionBlockFields,
  CAREERS_TEXT_ACCORDION_BLOCK_QUERY,
} from './careersTextAccordionBlock'
export {
  careersImageCarouselBlockFields,
  CAREERS_IMAGE_CAROUSEL_BLOCK_QUERY,
} from './careersImageCarouselBlock'
export {
  careersQuoteCarouselBlockFields,
  CAREERS_QUOTE_CAROUSEL_BLOCK_QUERY,
} from './careersQuoteCarouselBlock'
export {
  careersContentCarouselBlockFields,
  CAREERS_CONTENT_CAROUSEL_BLOCK_QUERY,
} from './careersContentCarouselBlock'
export {
  careersMarqueeBlockFields,
  CAREERS_MARQUEE_BLOCK_QUERY,
} from './careersMarqueeBlock'
export {
  careersBoxLinksBlockFields,
  CAREERS_BOX_LINKS_BLOCK_QUERY,
} from './careersBoxLinksBlock'
export {
  careersIconTextBlockFields,
  CAREERS_ICON_TEXT_BLOCK_QUERY,
} from './careersIconTextBlock'
export {
  careersLocationLinksBlockFields,
  CAREERS_LOCATION_LINKS_BLOCK_QUERY,
} from './careersLocationLinksBlock'
export {
  careersModalOverImageBlockFields,
  CAREERS_MODAL_OVER_IMAGE_BLOCK_QUERY,
} from './careersModalOverImageBlock'
export {
  careersFeatureBlockFields,
  CAREERS_FEATURE_BLOCK_QUERY,
} from './careersFeatureBlock'
export {
  careersVideoBlockFields,
  CAREERS_VIDEO_BLOCK_QUERY,
} from './careersVideoBlock'
export {
  careersCtaBlockFields,
  CAREERS_CTA_BLOCK_QUERY,
} from './careersCtaBlock'
export {
  careersBenefitsBlockFields,
  CAREERS_BENEFITS_BLOCK_QUERY,
} from './careersBenefitsBlock'
export {
  careersBorderedTextBlockFields,
  CAREERS_BORDERED_TEXT_BLOCK_QUERY,
} from './careersBorderedTextBlock'
export {
  careersRolesBlockFields,
  CAREERS_ROLES_BLOCK_QUERY,
} from './careersRolesBlock'
export {
  careersApplicationProcessBlockFields,
  CAREERS_APPLICATION_PROCESS_BLOCK_QUERY,
} from './careersApplicationProcessBlock'
export {
  careersPrefooterLinksBlockFields,
  CAREERS_PREFOOTER_LINKS_BLOCK_QUERY,
} from './careersPrefooterLinksBlock'
export {
  locationListBlockFields,
  LOCATION_LIST_BLOCK_QUERY,
} from './locationListBlock'
export { locationSearchFields, LOCATION_SEARCH_QUERY } from './locationSearch'
export {
  testimonialsBlockFields,
  TESTIMONIALS_BLOCK_QUERY,
} from './testimonials'

// Import field variables for use in composition (not exported)
import { heroSliderBannerFields } from './heroSliderBanner'
import { copyBlockFields } from './copyBlock'
import { imageStackFields } from './imageStack'
import { tabbedAccordionFields } from './tabbedAccordion'
import { pricePlansFields } from './pricePlans'
import { tabbedMediaCardBlockFields } from './tabbedMediaCardBlock'
import { featureBannerFields } from './featureBanner'
import { splitCopyImageBlockFields } from './splitCopyImageBlock'
import { fileContentFields } from './fileContent'
import { twoCardsBlockFields } from './twoCardsBlock'
import { puzzlePiecesBlockFields } from './puzzlePiecesBlock'
import { skillsBlockFields } from './skillsBlock'
import { quoteBlockFields } from './quoteBlock'
import { subjectSliderFields } from './subjectSlider'
import { videoHeroFields } from './videoHero'
import { postCardBlockFields } from './postCardBlock'
import { dayNurseriesFields } from './dayNurseries'
import { newsBlockFields } from './newsBlock'
import { contactFormBlockFields } from './contactFormBlock'
import { igPostsFields } from './igPosts'
import { expertCarouselFields } from './expertCarousel'
import { gridBlockFields } from './gridBlock'
import { nurserySearchBlockFields } from './nurserySearchBlock'
import { regionCardsBlockFields } from './regionCardsBlock'
import { careersJobListingsBlockFields } from './careersJobListingsBlock'
import { careersHeroBlockFields } from './careersHeroBlock'
import { careersRoleLinksBlockFields } from './careersRoleLinksBlock'
import { careersAfterHeroBlockFields } from './careersAfterHeroBlock'
import { careersTextBlockFields } from './careersTextBlock'
import { careersTextImageBlockFields } from './careersTextImageBlock'
import { careersTextCollageBlockFields } from './careersTextCollageBlock'
import { careersTextColumnsBlockFields } from './careersTextColumnsBlock'
import { careersTextAccordionBlockFields } from './careersTextAccordionBlock'
import { careersImageCarouselBlockFields } from './careersImageCarouselBlock'
import { careersQuoteCarouselBlockFields } from './careersQuoteCarouselBlock'
import { careersContentCarouselBlockFields } from './careersContentCarouselBlock'
import { careersMarqueeBlockFields } from './careersMarqueeBlock'
import { careersBoxLinksBlockFields } from './careersBoxLinksBlock'
import { careersIconTextBlockFields } from './careersIconTextBlock'
import { careersLocationLinksBlockFields } from './careersLocationLinksBlock'
import { careersModalOverImageBlockFields } from './careersModalOverImageBlock'
import { careersFeatureBlockFields } from './careersFeatureBlock'
import { careersVideoBlockFields } from './careersVideoBlock'
import { careersCtaBlockFields } from './careersCtaBlock'
import { careersBenefitsBlockFields } from './careersBenefitsBlock'
import { careersBorderedTextBlockFields } from './careersBorderedTextBlock'
import { careersRolesBlockFields } from './careersRolesBlock'
import { careersApplicationProcessBlockFields } from './careersApplicationProcessBlock'
import { careersPrefooterLinksBlockFields } from './careersPrefooterLinksBlock'
import { locationListBlockFields } from './locationListBlock'
import { locationSearchFields } from './locationSearch'
import { testimonialsBlockFields } from './testimonials'

/**
 * Composed content block fragment
 *
 * This replaces the old contentBlockFragment string.
 * It uses GROQ's select() to handle all block types.
 *
 * Usage:
 * ```groq
 * content.blockContent[]->{
 *   ${contentBlockFragment}
 * }
 * ```
 */
export const contentBlockFragment = `
  _id,
  _type,
  ...select(
    _type == "hero-slider-banner" => {
      ${heroSliderBannerFields}
    },
    _type == "copy-block" => {
      ${copyBlockFields}
    },
    _type == "image-stack" => {
      ${imageStackFields}
    },
    _type == "tabbed-accordion" => {
      ${tabbedAccordionFields}
    },
    _type == "price-plans" => {
      ${pricePlansFields}
    },
    _type == "tabbed-media-card-block" => {
      ${tabbedMediaCardBlockFields}
    },
    _type == "feature-banner" => {
      ${featureBannerFields}
    },
    _type == "split-copy-image-block" => {
      ${splitCopyImageBlockFields}
    },
    _type == "file-content" => {
      ${fileContentFields}
    },
    _type == "two-cards-block" => {
      ${twoCardsBlockFields}
    },
    _type == "puzzle-pieces-block" => {
      ${puzzlePiecesBlockFields}
    },
    _type == "skills-block" => {
      ${skillsBlockFields}
    },
    _type == "quote-block" => {
      ${quoteBlockFields}
    },
    _type == "subject-slider" => {
      ${subjectSliderFields}
    },
    _type == "video-hero" => {
      ${videoHeroFields}
    },
    _type == "post-card-block" => {
      ${postCardBlockFields}
    },
    _type == "day-nurseries" => {
      ${dayNurseriesFields}
    },
    _type == "news-block" => {
      ${newsBlockFields}
    },
    _type == "contact-form-block" => {
      ${contactFormBlockFields}
    },
    _type == "ig-posts" => {
      ${igPostsFields}
    },
    _type == "expert-carousel" => {
      ${expertCarouselFields}
    },
    _type == "grid-block" => {
      ${gridBlockFields}
    },
    _type == "nursery-search-block" => {
      ${nurserySearchBlockFields}
    },
    _type == "region-cards-block" => {
      ${regionCardsBlockFields}
    },
    _type == "careers-job-listings-block" => {
      ${careersJobListingsBlockFields}
    },
    _type == "careers-hero-block" => {
      ${careersHeroBlockFields}
    },
    _type == "careers-role-links-block" => {
      ${careersRoleLinksBlockFields}
    },
    _type == "careers-after-hero-block" => {
      ${careersAfterHeroBlockFields}
    },
    _type == "careers-text-block" => {
      ${careersTextBlockFields}
    },
    _type == "careers-text-image-block" => {
      ${careersTextImageBlockFields}
    },
    _type == "careers-text-collage-block" => {
      ${careersTextCollageBlockFields}
    },
    _type == "careers-text-columns-block" => {
      ${careersTextColumnsBlockFields}
    },
    _type == "careers-text-accordion-block" => {
      ${careersTextAccordionBlockFields}
    },
    _type == "careers-image-carousel-block" => {
      ${careersImageCarouselBlockFields}
    },
    _type == "careers-quote-carousel-block" => {
      ${careersQuoteCarouselBlockFields}
    },
    _type == "careers-content-carousel-block" => {
      ${careersContentCarouselBlockFields}
    },
    _type == "careers-marquee-block" => {
      ${careersMarqueeBlockFields}
    },
    _type == "careers-box-links-block" => {
      ${careersBoxLinksBlockFields}
    },
    _type == "careers-icon-text-block" => {
      ${careersIconTextBlockFields}
    },
    _type == "careers-location-links-block" => {
      ${careersLocationLinksBlockFields}
    },
    _type == "careers-modal-over-image-block" => {
      ${careersModalOverImageBlockFields}
    },
    _type == "careers-feature-block" => {
      ${careersFeatureBlockFields}
    },
    _type == "careers-video-block" => {
      ${careersVideoBlockFields}
    },
    _type == "careers-cta-block" => {
      ${careersCtaBlockFields}
    },
    _type == "careers-benefits-block" => {
      ${careersBenefitsBlockFields}
    },
    _type == "careers-bordered-text-block" => {
      ${careersBorderedTextBlockFields}
    },
    _type == "careers-roles-block" => {
      ${careersRolesBlockFields}
    },
    _type == "careers-application-process-block" => {
      ${careersApplicationProcessBlockFields}
    },
    _type == "careers-prefooter-links-block" => {
      ${careersPrefooterLinksBlockFields}
    },
    _type == "location-list-block" => {
      ${locationListBlockFields}
    },
    _type == "location-search" => {
      ${locationSearchFields}
    },
    _type == "testimonials-block" => {
      ${testimonialsBlockFields}
    }
  )
`

/**
 * Complete query for all content blocks
 * Used by TypeGen to generate a union type of all block results
 *
 * Note: This query fetches all block types, but in practice
 * you'll use contentBlockFragment within page/location queries.
 */
export const ALL_CONTENT_BLOCKS_QUERY = defineQuery(/* groq */ `
  *[_type in [
    "hero-slider-banner",
    "copy-block",
    "image-stack",
    "tabbed-accordion",
    "price-plans",
    "tabbed-media-card-block",
    "feature-banner",
    "split-copy-image-block",
    "file-content",
    "two-cards-block",
    "puzzle-pieces-block",
    "skills-block",
    "quote-block",
    "subject-slider",
    "video-hero",
    "post-card-block",
    "day-nurseries",
    "news-block",
    "contact-form-block",
    "ig-posts",
    "expert-carousel",
    "grid-block",
    "nursery-search-block",
    "region-cards-block",
    "careers-job-listings-block",
    "careers-hero-block",
    "careers-role-links-block",
    "careers-after-hero-block",
    "careers-text-block",
    "careers-text-image-block",
    "careers-text-collage-block",
    "careers-text-columns-block",
    "careers-text-accordion-block",
    "careers-image-carousel-block",
    "careers-quote-carousel-block",
    "careers-content-carousel-block",
    "careers-marquee-block",
    "careers-box-links-block",
    "careers-icon-text-block",
    "careers-location-links-block",
    "careers-modal-over-image-block",
    "careers-feature-block",
    "careers-video-block",
    "careers-cta-block",
    "careers-benefits-block",
    "careers-bordered-text-block",
    "careers-roles-block",
    "careers-application-process-block",
    "careers-prefooter-links-block",
    "location-list-block",
    "location-search",
    "testimonials-block"
  ]]{
    ${contentBlockFragment}
  }
`)

// Re-export types (will be available after running `npm run generate`)
// These will be auto-generated by Sanity TypeGen:
// - CONTACT_FORM_BLOCK_QUERYResult
// - EXPERT_CAROUSEL_QUERYResult
// - HERO_SLIDER_BANNER_QUERYResult
// ... (one for each block)
// - ALL_CONTENT_BLOCKS_QUERY_RESULT (union of all blocks)
